/**
 * Catálogo de columnas de la vista de lista (pantallas 34, 42 y 43).
 *
 * El engrane de columnas es exclusivo de la lista: en kanban no aparece. En la
 * vista Por Estatus la columna Estatus no se puede quitar ni reordenar (en el
 * diseño se ve fija y en gris).
 */

import { stateLabel } from './taskState'

export const LIST_COLUMNS = [
  { key: 'state', label: 'Estatus', align: 'left' },
  { key: 'title', label: 'Título', align: 'left' },
  { key: 'origin', label: 'Origen', align: 'center' },
  { key: 'evidences', label: 'Evidencias', align: 'left' },
  { key: 'assignee', label: 'Responsable', align: 'left' },
  { key: 'location', label: 'Ubicación', align: 'left' },
  { key: 'end_date', label: 'Fecha Límite', align: 'left' },
  { key: 'priority', label: 'Prioridad', align: 'left' },
  { key: 'region', label: 'Región', align: 'left' },
  { key: 'zone', label: 'Zona', align: 'left' },
  { key: 'district', label: 'Distrito', align: 'left' },
  { key: 'created_by', label: 'Creada por', align: 'left' },
  { key: 'tag', label: 'Categoría', align: 'left' },
  { key: 'tag2', label: 'Área', align: 'left' }
]

export const DEFAULT_LIST_COLUMNS = [
  'state', 'title', 'origin', 'evidences', 'assignee', 'location', 'end_date', 'priority'
]

/**
 * Columnas que no se pueden quitar ni mover, por vista.
 */
export function lockedColumnsFor (viewType) {
  return viewType === 'status' ? ['state'] : []
}

export function columnDefinition (key) {
  return LIST_COLUMNS.find(column => column.key === key) || null
}

/**
 * Resuelve la configuración guardada contra el catálogo: descarta claves que ya
 * no existen, garantiza que las bloqueadas estén presentes y en su lugar, y
 * cae al default cuando no hay nada guardado.
 */
export function resolveListColumns (saved, viewType) {
  const locked = lockedColumnsFor(viewType)
  const catalog = new Set(LIST_COLUMNS.map(column => column.key))

  let keys = Array.isArray(saved) && saved.length
    ? saved.filter(key => catalog.has(key))
    : DEFAULT_LIST_COLUMNS.slice()

  // Las bloqueadas van primero y siempre.
  keys = keys.filter(key => !locked.includes(key))
  keys = [...locked, ...keys]

  return keys
}

/**
 * Modelo del popup "Personalizar las columnas": el orden es el que se arrastra
 * y `selected` decide el check.
 */
export function buildColumnsDialogModel (visibleKeys, viewType) {
  const locked = lockedColumnsFor(viewType)
  const visible = new Set(visibleKeys)

  const ordered = [
    ...visibleKeys,
    ...LIST_COLUMNS.map(column => column.key).filter(key => !visible.has(key))
  ]

  return ordered.map(key => {
    const definition = columnDefinition(key)
    return {
      key,
      label: definition ? definition.label : key,
      selected: visible.has(key),
      locked: locked.includes(key)
    }
  })
}

export function columnsFromDialogModel (model, viewType) {
  const locked = lockedColumnsFor(viewType)
  const keys = model.filter(item => item.selected || locked.includes(item.key)).map(item => item.key)

  return resolveListColumns(keys, viewType)
}

const PRIORITY_TKEYS = {
  1: 'controlTower.priority_low',
  2: 'controlTower.priority_medium',
  3: 'controlTower.priority_high'
}

/**
 * Fecha corta del diseno (23/12/2023). El backend manda `end_date_local` con
 * hora; la tarjeta y la tabla solo muestran el dia.
 */
export function shortDate (value) {
  if (!value) return ''
  const [datePart] = String(value).split(' ')
  const [year, month, day] = datePart.split('-')
  if (!year || !month || !day) return String(value)
  return `${day}/${month}/${year}`
}

/**
 * Fecha con hora para el feed de conversacion: ahi el minuto importa porque
 * varios comentarios y reasignaciones pueden caer el mismo dia.
 */
export function shortDateTime (value) {
  if (!value) return ''
  const [datePart, timePart] = String(value).split(' ')
  const fecha = shortDate(datePart)
  if (!timePart) return fecha
  const [hour, minute] = timePart.split(':')
  return hour && minute ? `${fecha} ${hour}:${minute}` : fecha
}

/**
 * Folio corto del diseno ("No. 001"). Cuando la tarea no tiene `short_name` el
 * backend cae al id, que puede tener 10 digitos y descuadra tarjeta y tabla: en
 * ese caso se muestran los ultimos cuatro.
 */
export function shortFolio (task) {
  const raw = (task && task.folio) || ''
  if (!raw || (task && task.short_name)) return raw

  const [code, ...rest] = raw.split('-')
  return `${code}-${rest.join('-').slice(-4)}`
}

export function priorityTKey (priority) {
  return PRIORITY_TKEYS[Number(priority)] || null
}

const PRIORITY_LABELS = { 1: 'Baja', 2: 'Media', 3: 'Alta' }

/**
 * Etiqueta de prioridad. `t` es el traductor del componente; sin él cae al
 * español, que es el idioma por defecto del producto.
 */
export function priorityLabel (priority, t = null) {
  const key = priorityTKey(priority)
  if (!key) return '—'
  return t ? t(key) : PRIORITY_LABELS[Number(priority)]
}

export function priorityColor (priority) {
  switch (Number(priority)) {
    case 3: return '#FF5D5D'
    case 2: return '#FCC559'
    case 1: return '#61C66B'
    default: return '#B2B7C1'
  }
}

/**
 * Color del punto de prioridad en la tabla (Figma 348:7086): Alta #DD3A23,
 * Media #FCC559, Baja #8DBF66. La tarjeta del tablero usa su propia paleta.
 */
export function listPriorityColor (priority) {
  switch (Number(priority)) {
    case 3: return '#DD3A23'
    case 2: return '#FCC559'
    case 1: return '#8DBF66'
    default: return '#B2B7C1'
  }
}

/**
 * Valor plano de una celda. Lo usan la tabla y la exportación a Excel/CSV, para
 * que el archivo diga exactamente lo mismo que la tabla.
 */
export function cellValue (task, key, t = null) {
  switch (key) {
    case 'state': return stateLabel(task)
    case 'title': return task.title || ''
    case 'origin': return task.origin_label || ''
    case 'evidences': return Number(task.evidences_count || 0)
    case 'assignee': return task.assignee ? task.assignee.full_name : ''
    case 'location': return task.location ? task.location.name : ''
    case 'end_date': return shortDate(task.end_date || task.end_date_utc)
    case 'priority': return priorityLabel(task.priority, t)
    case 'region': return task.region || ''
    case 'zone': return task.zone || ''
    case 'district': return task.district || ''
    case 'created_by': return task.created_by ? task.created_by.full_name : ''
    case 'tag': return task.tag || ''
    case 'tag2': return task.tag2 || ''
    default: return ''
  }
}
